/**
 * Polynomial presets for the factor-tiles experiment.
 * These are the canned factoring problems students can work through.
 */

export interface FactorPreset {
  id: string;
  polynomial: string;
  advancedOnly: boolean;
}

export const FACTOR_PRESETS: readonly FactorPreset[] = [
  // Foundation presets (quadratic trinomials)
  { id: 'quad-simple-1', polynomial: 'x^2 + 5x + 6', advancedOnly: false },
  { id: 'quad-diff-squares', polynomial: 'x^2 - 9', advancedOnly: false },

  // Advanced presets
  { id: 'quad-leading-coeff', polynomial: '2x^2 + 7x + 3', advancedOnly: true },
  { id: 'cubic-diff', polynomial: 'x^3 - 8', advancedOnly: true },
  { id: 'quad-complete-square', polynomial: 'x^2 + 6x + 5', advancedOnly: true },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: FactorPreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // polynomial must be a non-empty string
  if (typeof p.polynomial !== 'string' || p.polynomial.length === 0) {
    return false;
  }

  // advancedOnly must be boolean
  if (typeof p.advancedOnly !== 'boolean') {
    return false;
  }

  return true;
}

/**
 * Check that presets are well-formed:
 * - All IDs are unique
 * - At least 2 foundation presets
 * - At least 3 advanced presets
 */
export function validatePresets(presets: readonly FactorPreset[]): boolean {
  const ids = new Set<string>();
  let foundationCount = 0;
  let advancedCount = 0;

  for (const p of presets) {
    if (!isValidPreset(p)) {
      return false;
    }
    if (ids.has(p.id)) {
      return false; // Duplicate ID
    }
    ids.add(p.id);

    if (p.advancedOnly) {
      advancedCount += 1;
    } else {
      foundationCount += 1;
    }
  }

  return foundationCount >= 2 && advancedCount >= 3;
}
